import React from 'react';
import DateRangeSelector from './DateRangeSelector';
import PayrollAreaSelector from './PayrollAreaSelector';
import WorkOrderStatusFilter from './WorkOrderStatusFilter';
import WorkOrderDepartmentFilter, { DEPARTMENT_OPTIONS } from './WorkOrderDepartmentFilter';
import WorkOrderCustomerFilter from './WorkOrderCustomerFilter';
import WorkOrderPropertyFilter from './WorkOrderPropertyFilter';
import WorkOrderPropertyManagementFilter from './WorkOrderPropertyManagementFilter';

const MAX_DATE_RANGE_DAYS = 31;

function WorkOrderFilterSection({
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  selectedStatuses,
  onStatusesChange,
  selectedDepartments,
  onDepartmentsChange,
  selectedAreas,
  onAreasChange,
  selectedCustomers,
  onCustomersChange,
  selectedProperties,
  onPropertiesChange,
  selectedPropertyManagement,
  onPropertyManagementChange,
  onGenerate,
  isGenerating
}) {
  // Convert department values to role strings for backend
  const getRolesFromDepartments = (departments) => {
    const roles = [];
    departments.forEach(deptValue => {
      const dept = DEPARTMENT_OPTIONS.find(d => d.value === deptValue);
      if (dept) {
        roles.push(...dept.roles);
      }
    });
    return roles;
  };

  const handleGenerate = () => {
    const roles = getRolesFromDepartments(selectedDepartments);
    onGenerate(roles);
  };

  const getDateRangeDays = () => {
    if (!startDate || !endDate) return 0;
    const start = new Date(startDate + 'T00:00:00');
    const end = new Date(endDate + 'T00:00:00');
    return Math.ceil((end - start) / (1000 * 60 * 60 * 24));
  };

  const dateRangeDays = getDateRangeDays();
  const isDateRangeTooLong = dateRangeDays > MAX_DATE_RANGE_DAYS;
  const canGenerate = selectedStatuses.length > 0 && startDate && endDate && !isDateRangeTooLong;

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <h4 className="text-md font-medium text-gray-900 mb-1">
          Work Order Report Filters
        </h4>
        <p className="text-sm text-gray-500">
          Generate reports for work orders with expense tracking
        </p>
      </div>

      {/* Date Range */}
      <div>
        <DateRangeSelector
          startDate={startDate}
          endDate={endDate}
          onStartDateChange={onStartDateChange}
          onEndDateChange={onEndDateChange}
        />
      </div>

      {/* Responsive filter grid: 3x2 desktop, 2x3 tablet, 1x6 mobile */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 border-t pt-6 items-start">
        <WorkOrderStatusFilter
          selectedStatuses={selectedStatuses}
          onChange={onStatusesChange}
        />
        <WorkOrderDepartmentFilter
          selectedDepartments={selectedDepartments}
          onChange={onDepartmentsChange}
        />
        <PayrollAreaSelector
          selectedAreas={selectedAreas}
          onChange={onAreasChange}
        />
        <WorkOrderCustomerFilter
          selectedCustomers={selectedCustomers || []}
          onChange={onCustomersChange}
        />
        <WorkOrderPropertyFilter
          selectedProperties={selectedProperties || []}
          onChange={onPropertiesChange}
        />
        <WorkOrderPropertyManagementFilter
          value={selectedPropertyManagement || ''}
          onChange={onPropertyManagementChange}
        />
      </div>

      {/* Generate Button */}
      <div className="border-t pt-6 flex items-center justify-between">
        <div className="text-sm text-gray-600">
          {isDateRangeTooLong && (
            <span className="text-red-600 font-medium">
              Date range cannot exceed {MAX_DATE_RANGE_DAYS} days. Currently {dateRangeDays} days — please narrow your date range.
            </span>
          )}
          {!isDateRangeTooLong && !canGenerate && (
            <span className="text-yellow-600">
              Select at least one status and a date range (max {MAX_DATE_RANGE_DAYS} days)
            </span>
          )}
          {canGenerate && (
            <span className="text-green-600">
              Ready to generate report ({dateRangeDays} day{dateRangeDays !== 1 ? 's' : ''})
            </span>
          )}
        </div>
        <button
          onClick={handleGenerate}
          disabled={!canGenerate || isGenerating}
          className="px-6 py-3 text-sm font-medium text-white bg-purple-600 border border-transparent rounded-md hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {isGenerating ? (
            <span className="flex items-center space-x-2">
              <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <span>Generating...</span>
            </span>
          ) : (
            'Generate Work Orders Report'
          )}
        </button>
      </div>

      {/* Filter Summary */}
      {canGenerate && (
        <div className="border-t pt-4">
          <h5 className="text-xs font-medium text-gray-700 mb-2">Active Filters:</h5>
          <div className="flex flex-wrap gap-2 text-xs">
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full bg-gray-100 text-gray-800">
              📅 {startDate} to {endDate}
            </span>
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full bg-purple-100 text-purple-800">
              📊 {selectedStatuses.length} status{selectedStatuses.length !== 1 ? 'es' : ''}
            </span>
            {selectedDepartments.length > 0 && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full bg-purple-100 text-purple-800">
                🏢 {selectedDepartments.length} department{selectedDepartments.length !== 1 ? 's' : ''}
              </span>
            )}
            {selectedAreas.length > 0 && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full bg-blue-100 text-blue-800">
                📍 {selectedAreas.length} area{selectedAreas.length !== 1 ? 's' : ''}
              </span>
            )}
            {selectedCustomers && selectedCustomers.length > 0 && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full bg-purple-100 text-purple-800">
                👥 {selectedCustomers.length} customer{selectedCustomers.length !== 1 ? 's' : ''}
              </span>
            )}
            {selectedProperties && selectedProperties.length > 0 && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full bg-purple-100 text-purple-800">
                🏠 {selectedProperties.length} propert{selectedProperties.length !== 1 ? 'ies' : 'y'}
              </span>
            )}
            {selectedPropertyManagement && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full bg-purple-100 text-purple-800">
                👤 Property management filter applied
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default WorkOrderFilterSection;
